import { Facebook, Instagram, Send, MessageCircle, Users, Bell } from "lucide-react";
import type { ArticleLang } from "@/lib/article-types";
import { useSocialLinks } from "@/lib/social-links";

/**
 * Social-media follow card shown in the desktop sidebar next to the
 * movie/series article. Counts are intentionally fake/placeholder.
 */
type Channel = {
  key: string;
  name: { ar: string; en: string; fr: string };
  href: string;
  followers: string;
  icon: React.ReactNode;
  bg: string;
  text: string;
};

const CHANNELS: Channel[] = [
  {
    key: "facebook",
    name: { ar: "فيسبوك", en: "Facebook", fr: "Facebook" },
    href: "https://facebook.com/",
    followers: "128K",
    icon: <Facebook className="h-5 w-5" />,
    bg: "bg-[#1877F2]",
    text: "text-white",
  },
  {
    key: "instagram",
    name: { ar: "إنستغرام", en: "Instagram", fr: "Instagram" },
    href: "https://instagram.com/",
    followers: "94.2K",
    icon: <Instagram className="h-5 w-5" />,
    bg: "bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]",
    text: "text-white",
  },
  {
    key: "x",
    name: { ar: "إكس (تويتر)", en: "X (Twitter)", fr: "X (Twitter)" },
    href: "https://x.com/",
    followers: "57.6K",
    icon: <XIcon />,
    bg: "bg-black",
    text: "text-white",
  },
  {
    key: "telegram",
    name: { ar: "تيليجرام", en: "Telegram", fr: "Telegram" },
    href: "https://t.me/",
    followers: "212K",
    icon: <Send className="h-5 w-5" />,
    bg: "bg-[#229ED9]",
    text: "text-white",
  },
  {
    key: "whatsapp",
    name: { ar: "واتساب", en: "WhatsApp", fr: "WhatsApp" },
    href: "https://wa.me/",
    followers: "36.8K",
    icon: <MessageCircle className="h-5 w-5" />,
    bg: "bg-[#25D366]",
    text: "text-white",
  },
];

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
      <path d="M18.244 2H21l-6.49 7.41L22.5 22h-6.86l-5.37-6.97L3.96 22H1.2l6.95-7.94L1.5 2h7.02l4.85 6.39L18.24 2zm-2.4 18h1.92L8.25 4H6.24l9.6 16z" />
    </svg>
  );
}

export function SocialFollowBox({ lang = "ar" }: { lang?: ArticleLang }) {
  const t = (ar: string, en: string, fr: string) =>
    lang === "ar" ? ar : lang === "fr" ? fr : en;
  const [links] = useSocialLinks();
  const channels = CHANNELS
    .map((c) => ({ ...c, href: (links as Record<string,string|undefined>)[c.key] || c.href }))
    .filter((c) => !!c.href);

  const totalLabel = t("إجمالي المتابعين", "Total followers", "Total des abonnés");
  const followLabel = t("متابعة", "Follow", "Suivre");
  const headerTitle = t("تابعنا على", "Follow us on", "Suivez-nous");
  const headerSub = t(
    "انضم لأكثر من 500K متابع",
    "Join 500K+ followers",
    "Rejoignez 500K+ abonnés",
  );

  return (
    <aside
      className="rounded-2xl border border-border bg-card shadow-card overflow-hidden"
      aria-label={headerTitle}
    >
      {/* Header */}
      <div
        className="p-4 text-primary-foreground"
        style={{ background: "var(--gradient-primary)" }}
      >
        <div className="flex items-center gap-2 font-extrabold text-lg">
          <Bell className="h-5 w-5" />
          <span>{headerTitle}</span>
        </div>
        <p className="text-xs opacity-90 mt-1">{headerSub}</p>
      </div>

      {/* Channels */}
      <ul className="p-3 space-y-2">
        {channels.map((c) => (
          <li key={c.key}>
            <a
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-xl border border-border bg-background/50 hover:bg-accent transition p-2.5 group"
            >
              <div
                className={`grid place-items-center w-10 h-10 rounded-lg shrink-0 ${c.bg} ${c.text}`}
                aria-hidden
              >
                {c.icon}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm truncate">{c.name[lang as "ar" | "en" | "fr"] || c.name.ar}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  <span>{c.followers} {t("متابع", "followers", "abonnés")}</span>
                </div>
              </div>
              <span className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-primary/10 text-primary border border-primary/30 group-hover:bg-primary group-hover:text-primary-foreground transition">
                {followLabel}
              </span>
            </a>
          </li>
        ))}
      </ul>

      {/* Footer total */}
      <div className="px-4 py-3 border-t border-border bg-background/30 flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{totalLabel}</span>
        <span className="font-extrabold text-primary">528.6K+</span>
      </div>
    </aside>
  );
}
